import React, { useEffect, useRef, useState } from "react";
import { Box, Text } from "grommet";
import BasePaymentForm from "./BasePaymentForm";
import Loading from "../Loading";
import Button from "../Button";
import StatusIcon from "../StatusIcon";
import { formatCurrency } from "../../utils/numbers";
import { recordCapturedPayment, reportUnrecordedPayment } from "../../utils/checkoutPayment";

/*
    Rendered once Stripe has confirmed the payment and before our backend has recorded
    it. At this point the customer has already been charged, so this screen deliberately
    offers no way to pay again — its only job is to land the record call.
*/
const PaymentFinalizing = ({ capturedPayment, onRecorded, recordUrl, hasServerSideRecovery, copy, showUnrecordedNotice = true }) => {
    const { uuid, slug, recordBody, confirmation } = capturedPayment || {};
    const { amount, currency, id: confirmationId, intent_id: intentId } = confirmation || {};

    const [attemptsFailed, setAttemptsFailed] = useState(0);
    const [hasGivenUp, setHasGivenUp] = useState(false);
    const [retryToken, setRetryToken] = useState(0);
    const hasReportedRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setHasGivenUp(false);

        recordCapturedPayment({
            // Callers on another payment flow (the deposit payer page) pass their own
            // path. Default keeps the invoice checkout exactly as it was.
            url: recordUrl || `/api/checkout/${uuid}/${slug}`,
            body: recordBody,
            isCancelled: () => cancelled,
            onAttemptFailed: ({ attempt, error }) => {
                if (cancelled) return;
                setAttemptsFailed(attempt);
                // Report the first failure only: enough to make the 502/504 rate visible
                // without one flaky payment flooding Honeybadger.
                if (!hasReportedRef.current) {
                    hasReportedRef.current = true;
                    reportUnrecordedPayment({ uuid, slug, intentId, attempt, error });
                }
            }
        })
            .then(data => {
                if (cancelled || !data) return;
                onRecorded(data);
            })
            .catch(error => {
                if (cancelled) return;
                reportUnrecordedPayment({ uuid, slug, intentId, error, exhausted: true });
                setHasGivenUp(true);
            });

        return () => {
            cancelled = true;
        };
    }, [uuid, slug, retryToken]);

    // Closing the tab mid-record is how a settlement goes missing. Warn while in flight.
    useEffect(() => {
        if (hasGivenUp) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [hasGivenUp]);

    /*
        Every string here is overridable, and every default is the invoice checkout's
        existing wording. The deposit payer page says something different - "received"
        overstates a payment we have captured but not yet recorded - and it names the
        merchant rather than our support address, because its payer has no relationship
        with us. Callers that pass no `copy` render exactly what they rendered before.
    */
    const {
        heading: headingCopy = 'Payment received',
        lead: leadCopy,
        reassurance: reassuranceCopy,
        pendingNote: pendingNoteCopy,
        helpLine: helpLineCopy,
        givenUpHeading,
        givenUpLead,
        retryLabel = 'Try again',
    } = copy || {};

    const paidLine = leadCopy || (amount
        ? `We've received your payment of ${formatCurrency(amount, currency)}.`
        : "We've received your payment.");

    /*
        The two states this component can be in are not the same story, and telling them
        with one set of words made the page contradict itself: "we're finishing up now"
        above "contact support" and a Try again button, so the payer could not tell
        whether to wait, pay again, or call someone. While retries are in flight the
        payment is genuinely still in progress. Once they are exhausted the payment has
        certainly gone through and only our record of it has not, which is a different
        thing to say and a different thing to ask of them. Callers that override neither
        get the invoice checkout's original single voice.
    */
    const givenUpHeadingCopy = givenUpHeading || headingCopy;
    const givenUpLine = givenUpLead || paidLine;

    /*
        Card checkouts have a server-side backstop: ReconcileCheckoutPaymentIntents polls
        Stripe and records anything the confirm never landed, so promising the customer an
        automatic receipt is honest. Bank debit has no equivalent reconciler yet (every
        scope there is payment_type: 'card'), so those customers need a human.
    */
    const canRecoverServerSide = hasServerSideRecovery === undefined
        ? slug === 'card'
        : hasServerSideRecovery;

    /*
        Exhausted retries are our failure, not the payer's, and the notice below tells
        them their money moved while our system does not know it - then hands them a
        reference and asks them to email someone. On a page whose payer has no account
        and no support relationship that is frightening and actionable by nobody. A
        caller can suppress it and keep showing the in-progress screen; the escalation
        still fires either way, so the failure is flagged where it can be acted on
        rather than at the customer.
    */
    if (hasGivenUp && showUnrecordedNotice) {
        return (
            <BasePaymentForm
                heading={givenUpHeadingCopy}
                primaryButton={(
                    <Button
                        event={{ section: 'payment_finalizing', element: 'retry_record' }}
                        primary
                        label={retryLabel}
                        key="retry"
                        onClick={() => {
                            setAttemptsFailed(0);
                            setRetryToken(token => token + 1);
                        }}
                        data-testid="checkout-finalizing-retry"
                    />
                )}
                fill
            >
                <Box margin={{ top: 'medium' }} gap="small" align="start">
                    <StatusIcon type="warning" size="large" />
                    <Text size="large" weight={500}>{givenUpLine}</Text>
                    {reassuranceCopy ? (<Text>{reassuranceCopy}</Text>) : (
                        <Text>
                            We&apos;re still finalizing your receipt. Your payment went through and you
                            have not been charged twice, so please do not submit it again.
                        </Text>
                    )}
                    {canRecoverServerSide ? (
                        <Text>
                            You can safely leave this page. We&apos;ll finish recording your
                            payment automatically and email your receipt.
                        </Text>
                    ) : helpLineCopy ? (<Text>{helpLineCopy}</Text>) : (
                        <Text>
                            Please contact help@streamlinedpayments.com so we can finish
                            recording it
                            {confirmationId ? ', quoting the reference below.' : '.'}
                        </Text>
                    )}
                    {confirmationId && (
                        <Box align="start">
                            <Text>Reference</Text>
                            <Text size="small" monospaced>{confirmationId}</Text>
                        </Box>
                    )}
                </Box>
            </BasePaymentForm>
        );
    }

    return (
        <BasePaymentForm heading={headingCopy} fill>
            <Box margin={{ top: 'medium' }} gap="small" align="start" data-testid="checkout-finalizing">
                <Box height="xxsmall" flex={false} alignSelf="start">
                    <Loading plain />
                </Box>
                <Text size="large" weight={500}>{paidLine}</Text>
                <Text>{pendingNoteCopy || 'Finalizing your receipt. Please keep this page open.'}</Text>
                {attemptsFailed > 0 && !hasGivenUp && (
                    <Text size="small" color="text-weak">
                        This is taking longer than usual. We&apos;re still working on it. Your
                        payment is safe and won&apos;t be taken twice.
                    </Text>
                )}
            </Box>
        </BasePaymentForm>
    );
};

export default PaymentFinalizing;
